import React, { useState, useEffect, useCallback } from 'react';
import { Comment } from '../types';
import { getComments, postComment, getCurrentUser } from '../services/api';

interface MessageCommentsProps {
  messageId: number;
}

const MessageComments: React.FC<MessageCommentsProps> = ({ messageId }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);

  const loadComments = useCallback(async () => {
    try {
      const data = await getComments(messageId);
      setComments(data);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // Reload when something posts a comment out-of-band for THIS thread — e.g. Sally's
  // server-composed "needs help" note, which lands a few seconds after the thread
  // opens (a grounded model call). Without this the panel would show it only on the
  // next reopen. Filtered on messageId so unrelated threads don't refetch.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && Number(detail.messageId) === Number(messageId)) {
        loadComments();
      }
    };
    window.addEventListener('sl-comments-refresh', handler);
    return () => window.removeEventListener('sl-comments-refresh', handler);
  }, [messageId, loadComments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const u = getCurrentUser();
      await postComment(messageId, {
        userEmail: u.email || 'unknown@jandjbagging.com',
        userName: u.name || 'Unknown User',
        commentText: newComment
      });
      setNewComment('');
      loadComments();
    } catch (err) {
      console.error('Failed to post comment:', err);
    }
  };

  return (
    <div className="border-t border-gray-200 bg-gray-50 p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">Comments</h4>
      
      {loading ? (
        <div className="text-sm text-gray-500">Loading...</div>
      ) : (
        <div className="space-y-3 mb-4">
          {comments.length === 0 ? (
            <p className="text-sm text-gray-500">No comments yet</p>
          ) : (
            comments.map(comment => {
              // Sally's own log entries are styled distinctly (purple + spark) so
              // the thread's AI history reads apart from human comments.
              const isSally = comment.user_name === 'Sally Sales';
              return (
                <div
                  key={comment.id}
                  className={
                    'rounded p-3 shadow-sm ' +
                    (isSally
                      ? 'bg-purple-50 border border-purple-100'
                      : 'bg-white')
                  }
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={
                        'text-sm font-medium inline-flex items-center gap-1 ' +
                        (isSally ? 'text-purple-800' : 'text-gray-900')
                      }
                    >
                      {isSally && (
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l1.9 4.8L18.7 8l-3.8 3.1L16 16l-4-2.6L8 16l1.1-4.9L5.3 8l4.8-1.2z" />
                        </svg>
                      )}
                      {comment.user_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(comment.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className={'text-sm ' + (isSally ? 'text-purple-900' : 'text-gray-700')}>
                    {comment.comment_text}
                  </p>
                </div>
              );
            })
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex space-x-2">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-brand-700 rounded-lg hover:bg-brand-800"
        >
          Post
        </button>
      </form>
    </div>
  );
};

export default MessageComments;
